import React from 'react';
import { Badge, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Avatar from '../../../../../atomic/molecules/avatar/Avatar';
import { useSimilarPeopleQuery } from '../../../__generated__/graphql';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { getEmployeeInitialsFromName } from '../../../utils/employeeInitials';
import { managerRoutes } from '../../routes';

const MAX_SHARED_SKILLS = 3;
const MAX_VISIBLE_MATCHES = 5;

type Props = {
  employeeId: string;
};

export const SimilarPeopleCard: React.FC<React.PropsWithChildren<Props>> = ({
  employeeId,
}) => {
  const t = useNamespacedTranslation('skills.similarPeopleCard');
  const { data, loading } = useSimilarPeopleQuery({
    variables: { employeeId },
  });

  if (loading) {
    return null;
  }

  const similar = data?.manager.employee?.similarEmployees ?? [];
  if (similar.length === 0) {
    return null;
  }

  const visible = similar.slice(0, MAX_VISIBLE_MATCHES);
  const hiddenCount = similar.length - visible.length;

  return (
    <Card className="mt-3">
      <Card.Header>
        <div className="fw-bold">{t('header')}</div>
      </Card.Header>
      <Card.Body className="d-flex flex-column gap-3">
        {visible.map((person) => (
          <Link
            key={person.Id}
            to={managerRoutes.employeePage.route.create({
              employeeId: person.Id,
            })}
            className="d-flex align-items-center gap-3 text-decoration-none text-reset">
            <Avatar initials={getEmployeeInitialsFromName(person.name)} />
            <div className="flex-grow-1">
              <div className="fw-semibold">{person.name}</div>
              {person.sharedSkills.length > 0 && (
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {person.sharedSkills.slice(0, MAX_SHARED_SKILLS).map((s) => (
                    <Badge key={s.Id} bg="light" text="dark">
                      {s.name}
                    </Badge>
                  ))}
                  {person.sharedSkills.length > MAX_SHARED_SKILLS && (
                    <Badge bg="light" text="dark">
                      {t('moreSkills', {
                        count: person.sharedSkills.length - MAX_SHARED_SKILLS,
                      })}
                    </Badge>
                  )}
                </div>
              )}
            </div>
            <Badge bg="primary" pill>
              {t('matchPercentage', {
                value: Math.round(person.matchPercentage),
              })}
            </Badge>
          </Link>
        ))}
        {hiddenCount > 0 && (
          <div className="text-muted small">
            {t('moreMatches', { count: hiddenCount })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};
